/**
 * MainLayout.tsx
 * =============================================================================
 * Global layout component that wraps all routes.
 * Maintains persistent timer logic and displays MiniPlayer when needed.
 *
 * CRITICAL: Timer tick logic lives here to survive route changes.
 *
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { usePlayerStore } from '../../store/playerStore';
import { MiniPlayer } from '../player/MiniPlayer';

export function MainLayout() {
  const location = useLocation();

  // Timer state from store
  const timerState = usePlayerStore((s) => s.timerState);
  const remainingTime = usePlayerStore((s) => s.remainingTime);
  const tickSessionTime = usePlayerStore((s) => s.tickSessionTime);
  const tickRemainingTime = usePlayerStore((s) => s.tickRemainingTime);

  // Audio state
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const isPaused = usePlayerStore((s) => s.isPaused);
  const currentMode = usePlayerStore((s) => s.currentMode);

  // Actions needed to stop audio and timer when the countdown reaches zero
  const stop = usePlayerStore((s) => s.stop);
  const setTimerState = usePlayerStore((s) => s.setTimerState);

  // Pomodoro cycling
  const pomodoroPhase = usePlayerStore((s) => s.pomodoroPhase);
  const cyclePomodoroPhase = usePlayerStore((s) => s.cyclePomodoroPhase);

  // Ref to hold interval (number in browser, not NodeJS.Timeout)
  const intervalRef = useRef<number | null>(null);

  // =========================================================================
  // CRITICAL: GLOBAL TIMER TICK LOGIC
  // This runs at the layout level, so it survives route changes
  // =========================================================================
  useEffect(() => {
    const isActive = timerState === 'running' || timerState === 'break';

    if (isActive) {
      // Start interval: tick every second
      intervalRef.current = setInterval(() => {
        // Always tick session time (counts up)
        tickSessionTime();

        // Tick remaining time (counts down) if > 0
        if (remainingTime > 0) {
          tickRemainingTime();
        }
      }, 1000);

    } else {
      // Clear interval when not active
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, remainingTime, tickSessionTime, tickRemainingTime]);

  // =========================================================================
  // TIMER END DETECTION
  // When the countdown reaches exactly 0 while the timer was actively running:
  // - Pomodoro/Ultradian (profiles with breaks): cycle to next phase, keep audio
  // - Other profiles: stop audio and reset timer to idle
  // =========================================================================
  useEffect(() => {
    // Guard: only act when the countdown just hit zero during an active session
    if (remainingTime !== 0) return;
    if (timerState !== 'running') return;

    // Pomodoro cycle: if we're in a cycling profile (work/break), advance phase
    if (pomodoroPhase === 'work' || pomodoroPhase === 'break') {
      cyclePomodoroPhase();
      // Timer stays 'running' — audio keeps playing, remainingTime resets
      return;
    }

    // Non-cycling profile: stop audio and reset
    stop();
    setTimerState('idle');
  }, [remainingTime, timerState, pomodoroPhase, stop, setTimerState, cyclePomodoroPhase]);

  // =========================================================================
  // MINI PLAYER VISIBILITY LOGIC
  // Show MiniPlayer when:
  // - NOT on home page (/), AND
  // - (Audio is playing OR timer is running OR session has progress)
  // =========================================================================
  const isHomePage = location.pathname === '/';
  const hasActiveSession = isPlaying || isPaused || timerState !== 'idle';
  const shouldShowMiniPlayer = !isHomePage && hasActiveSession && currentMode;

  return (
    <>
      {/* Route Content */}
      <Outlet />

      {/* Mini Player - Floats when not on home */}
      {shouldShowMiniPlayer && <MiniPlayer />}
    </>
  );
}

export default MainLayout;
